import { type CSSProperties, type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isMfaChallenge, login, verifyMfaChallenge, MfaVerifyError, type LoginResponse, type MfaChallenge } from '../api/auth'
import { mfaErrorMessage } from '../api/mfa'
import BrandLogo from '../components/BrandLogo'
import { MfaCodeInput } from '../components/MfaCodeInput'
import { Button, ButtonLink, Card, CardContent, Callout, Checkbox, TextField, IconButton } from '../components/ui'
import { SandboxBanner } from '../layout/SandboxBanner'
import { clearLoggedOut, markAutoRedirectTried, shouldAutoRedirect, ssoErrorMessage, startEqualPswLogin } from '../lib/equalPswSso'

/**
 * La pagina sta fuori dalla shell: gestisce da sola le aree sicure e i segmenti dello schermo. Con due segmenti
 * affiancati il modulo sta nel segmento 1 e non attraversa mai la cerniera; senza segmenti --md-seg1-* valgono
 * l'intero viewport. Con segmenti sovrapposti (tabletop) sta nel segmento superiore.
 */
const PAGE_STYLE: CSSProperties = {
  minHeight: 'min(100svh, var(--md-seg0-height))',
  paddingTop: 'var(--md-safe-top)',
  paddingBottom: 'var(--md-safe-bottom)',
  paddingLeft: 'max(var(--md-safe-left), var(--md-seg1-left))',
  paddingRight: 'max(var(--md-safe-right), calc(100vw - var(--md-seg1-left) - var(--md-seg1-width)))',
}
const THEME_TOGGLE_STYLE: CSSProperties = {
  top: 'max(16px, var(--md-safe-top))',
  insetInlineEnd: 'max(16px, var(--md-safe-right), calc(100vw - var(--md-seg1-left) - var(--md-seg1-width) + 16px))',
}

interface Props {
  onLogin: (auth: LoginResponse) => void
}

/**
 * F-040 / REQ-032-002. Codes that mean "this challenge is dead, go back to step one" rather than
 * "wrong code, try again" — the distinction matters because `MfaService.VerifyLoginChallengeAsync`
 * only calls `challenge.MarkUsed()` on a CORRECT code (confirmed by reading it): a wrong TOTP/
 * recovery code does not consume the challenge, so staying on step two and letting the user retry
 * is both possible and correct for `MFA_INVALID_CODE`. `INVALID_CHALLENGE` (unknown/expired/
 * already-used), `ACCOUNT_LOCKED` and `ACCOUNT_DEACTIVATED` all mean the account is not getting in
 * this way no matter how many more codes are tried, so those three send the user back to step one
 * with an explanation instead of leaving them on a dead screen.
 */
const DEAD_END_MFA_CODES = new Set(['INVALID_CHALLENGE', 'ACCOUNT_LOCKED', 'ACCOUNT_DEACTIVATED'])

export function LoginPage({ onLogin: onLoginProp }: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  // Equal PSW: ritorno con errore (/login?error=…) e sfida del secondo fattore passata da SsoPage.
  const ssoError = new URLSearchParams(location.search).get('error')
  const ssoChallenge = (location.state as { mfaChallenge?: MfaChallenge } | null)?.mfaChallenge ?? null
  // Rinvio automatico al vault: una volta per sessione del browser, mai con ?error= o dopo un logout.
  const [autoRedirecting] = useState(() => !ssoChallenge && shouldAutoRedirect(location.search))

  function onLogin(user: LoginResponse) {
    clearLoggedOut()
    onLoginProp(user)
  }

  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || 'dark',
  )
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState<string | null>(() => (ssoError ? ssoErrorMessage(ssoError) : null))
  const [loading, setLoading] = useState(false)

  // F-040: la prova temporanea del secondo fattore vive SOLO in questo state — mai nell'URL, in
  // localStorage o in sessionStorage — così un refresh o un cambio pagina la fa sparire invece di
  // lasciarla riscattabile in background (il backend la scarta comunque dopo 5 minuti o al primo
  // uso corretto, ma qui non deve sopravvivere nemmeno quel tanto).
  const [challenge, setChallenge] = useState<MfaChallenge | null>(ssoChallenge)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [mfaLoading, setMfaLoading] = useState(false)
  const mfaCodeInputId = useId()
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (autoRedirecting) {
      markAutoRedirectTried()
      startEqualPswLogin()
    }
  }, [autoRedirecting])

  // La sfida arrivata da SsoPage vive solo nello state di React (come quella del login con password):
  // la si toglie subito dalla cronologia, così un ricaricamento non la ritrova.
  useEffect(() => {
    if (ssoChallenge) navigate(location.pathname + location.search, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleEqualPsw() {
    markAutoRedirectTried()
    startEqualPswLogin()
  }

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.setAttribute('data-theme', next)
    try { localStorage.setItem('ecrm_theme_mode', next) } catch { /* ignore */ }
    setTheme(next)
  }

  function backToLogin(message: string) {
    if (expiryTimerRef.current !== null) clearTimeout(expiryTimerRef.current)
    setChallenge(null)
    setMfaCode('')
    setMfaError(null)
    setPassword('')
    setError(message)
  }

  // La prova dura 5 minuti sul backend (MfaService.LoginChallengeTtl): se l'utente lascia la
  // schermata aperta oltre la scadenza senza inviare nulla, questo timer lo riporta al login con un
  // messaggio chiaro invece di lasciarlo su un modulo che sembra ancora valido ma non lo è più.
  useEffect(() => {
    if (!challenge) return
    const msRemaining = new Date(challenge.expiresAt).getTime() - Date.now()
    expiryTimerRef.current = setTimeout(
      () => backToLogin('La verifica è scaduta. Accedi di nuovo.'),
      Math.max(msRemaining, 0),
    )
    return () => {
      if (expiryTimerRef.current !== null) clearTimeout(expiryTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await login(email, password, rememberMe)
      if (isMfaChallenge(result)) {
        setChallenge(result)
        setMfaCode('')
        setMfaError(null)
      } else {
        onLogin(result)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login fallito. Controlla le credenziali.')
    } finally {
      setLoading(false)
    }
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault()
    if (!challenge) return
    setMfaError(null)
    setMfaLoading(true)
    try {
      const user = await verifyMfaChallenge(challenge.challengeToken, mfaCode, rememberMe)
      onLogin(user)
    } catch (err) {
      const code = err instanceof MfaVerifyError ? err.code : undefined
      const message = mfaErrorMessage(err)
      if (code && DEAD_END_MFA_CODES.has(code)) {
        backToLogin(message)
      } else {
        setMfaError(message)
        setMfaCode('')
      }
    } finally {
      setMfaLoading(false)
    }
  }

  return (
    <main style={PAGE_STYLE} className="d-flex align-items-center justify-content-center position-relative md-u-surface-low">
      <div style={{ position: "absolute", top: 0, left: 0, right: 0 }}><SandboxBanner /></div>
      <h1 className="md-sr-only md-typescale-headline-small">Accedi a EqualCRM</h1>
      {/* Theme toggle */}
      <IconButton
        icon={theme === 'dark' ? 'light_mode' : 'dark_mode'}
        variant="tonal"
        aria-label="Attiva/disattiva tema chiaro/scuro"
        onClick={toggleTheme}
        style={THEME_TOGGLE_STYLE}
        className="position-fixed z-3"
      />

      {/* Card wrapper */}
      <div className="w-100 p-3 md-u-maxw-432">
        <Card variant="outlined" padding="md">
          <CardContent>

            {/* Brand */}
            <div className="text-center mb-4">
              <BrandLogo height={64} />
              <p className="text-muted md-typescale-body-small mt-1 mb-0">
                Management Suite
              </p>
            </div>

            {autoRedirecting && !error && (
              <Callout tone="info" role="status" className="mb-3">
                Accesso con Equal PSW in corso…
              </Callout>
            )}

            {/* Error alert */}
            {error && (
              <Callout tone="error" role="alert" className="mb-3">
                {error}
              </Callout>
            )}

            {challenge ? (
              /* F-040 / REQ-032-002: secondo passo del login — la password è già stata
                 verificata, resta solo il secondo fattore. */
              <form onSubmit={(e) => { void handleMfaSubmit(e) }} aria-label="Verifica del secondo fattore">
                <p className="text-muted md-typescale-body-small">
                  Il tuo account richiede un secondo fattore. Inserisci il codice della tua app di
                  autenticazione, oppure un codice di recupero se non hai accesso all'app.
                </p>

                {mfaError && (
                  <Callout tone="error" role="alert" id={`${mfaCodeInputId}-error`} className="mb-3">
                    {mfaError}
                  </Callout>
                )}

                <div className="mb-3">
                  <MfaCodeInput
                    id={mfaCodeInputId}
                    value={mfaCode}
                    onChange={setMfaCode}
                    disabled={mfaLoading}
                    autoFocus
                    ariaDescribedBy={mfaError ? `${mfaCodeInputId}-error` : undefined}
                    ariaInvalid={!!mfaError}
                  />
                </div>

                <Button
                  variant="filled"
                  fullWidth
                  type="submit"
                  disabled={mfaLoading || !mfaCode}
                  className="mb-3"
                >
                  {mfaLoading ? 'Verifica…' : 'Verifica e accedi'}
                </Button>

                <div className="text-center">
                  <Button
                    variant="text"
                    size="sm"
                    type="button"
                    onClick={() => backToLogin('')}
                    disabled={mfaLoading}
                  >
                    Torna al login
                  </Button>
                </div>
              </form>
            ) : (
              /* Login form */
              <>
              <Button
                variant="filled"
                fullWidth
                type="button"
                icon="key"
                onClick={handleEqualPsw}
                disabled={loading}
                className="mb-2"
              >
                Accedi con Equal PSW
              </Button>
              <p className="text-muted md-typescale-body-small text-center mb-3">
                oppure con email e password
              </p>
              <form onSubmit={(e) => { void handleSubmit(e) }}>
                <div className="mb-3">
                  <TextField
                    id="login-email"
                    label="Email"
                    type="email"
                    variant="outlined"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>

                <div className="mb-3 position-relative">
                  <TextField
                    id="login-password"
                    label="Password"
                    type={showPw ? 'text' : 'password'}
                    variant="outlined"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <IconButton
                    icon={showPw ? 'visibility_off' : 'visibility'}
                    size="sm"
                    aria-label={showPw ? 'Nascondi password' : 'Mostra password'}
                    onClick={() => setShowPw(!showPw)}
                    className="position-absolute top-0 end-0 mt-3 me-2"
                  />
                </div>

                <div className="mb-3">
                  <Checkbox
                    id="login-remember"
                    label="Ricordami per 30 giorni"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                </div>

                <Button
                  variant="tonal"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  className="mb-3"
                >
                  {loading ? 'Accesso in corso…' : 'Accedi'}
                </Button>

                <div className="text-center">
                  <ButtonLink to="/forgot-password" variant="text" size="sm">
                    Password dimenticata?
                  </ButtonLink>
                </div>
              </form>
              </>
            )}

          </CardContent>
        </Card>
      </div>
    </main>
  )
}
